import React, { useState } from 'react'
import { XMarkIcon, InformationCircleIcon } from '@heroicons/react/24/outline'
import { Order, Payment } from '../types/order'
import { PLATFORM_NAMES } from '../lib/orderSource'
import { formatEuros } from '../lib/format'

/**
 * Record a refund that was made OUTSIDE our system — in the Mollie dashboard for a webshop
 * order, by Uber Eats / Thuisbezorgd for a platform order — so the books match. Nothing is
 * triggered from here (owner 2026-10-09). Bookkeeping, not a terminal refund: the only rules
 * are the API's — a whole number of cents above zero and never more than what is still paid.
 * No 50 % / €300 / once-per-order caps (those are terminal-risk caps) and no reason field.
 * Contract: API docs/frontend/payment-integration-guide.md § "Recording a refund made outside
 * our system (2026-10-09)".
 *
 * Mounted only while open (the parent renders it conditionally), so state starts fresh each time.
 */

interface ExternalRefundModalProps {
  onClose: () => void
  order: Order
  /** Resolves when the refund row is written; rejects with the API's message (shown inline). */
  onConfirm: (amountCents: number) => Promise<void>
  isLoading?: boolean
}

/** Who refunded the customer — fixed by the order, never chosen here. */
export const externalRefundProvider = (order: Pick<Order, 'order_source' | 'platform'>): string => {
  if (order.order_source === 'WEBSHOP') return 'Mollie'
  return (order.platform && PLATFORM_NAMES[order.platform]) || 'the platform'
}

/** "in the Mollie dashboard" / "by Thuisbezorgd" — one copy for the modal and the page. */
export const refundedWhere = (order: Pick<Order, 'order_source' | 'platform'>): string =>
  order.order_source === 'WEBSHOP' ? 'in the Mollie dashboard' : `by ${externalRefundProvider(order)}`

/** Label for a refund row's `provider` ('mollie' / 'uber_eats' / 'thuisbezorgd' / 'hubrise'). */
export const refundProviderLabel = (
  provider: string | undefined | null,
  order: Pick<Order, 'order_source' | 'platform'>
): string => {
  if (provider === 'mollie') return 'Mollie'
  if (provider && PLATFORM_NAMES[provider]) return PLATFORM_NAMES[provider]
  return externalRefundProvider(order)
}

/**
 * Was this refund row recorded through recordExternalRefund? Keyed on the ROW, not the channel:
 * the API stamps `external-refund-<saleId>-<ts>` as transaction_id (its provider_status
 * 'external' is not exposed over GraphQL). A HubRise order refunded earlier through the
 * terminal path's no-money `online` branch carries the sale's transaction id and must not be
 * labelled as refunded by the platform.
 */
export const isExternallyRecordedRefund = (payment: Pick<Payment, 'transaction_type' | 'transaction_id'>): boolean =>
  (payment.transaction_type === 'REFUND' || payment.transaction_type === 'PARTIAL_REFUND') &&
  (payment.transaction_id ?? '').startsWith('external-refund-')

/** Completed money on the order: sales in, refunds out, what is still paid. */
export const externalRefundTotals = (order: Pick<Order, 'payments'>) => {
  const completed: Payment[] = (order.payments ?? []).filter(p => p.status === 'COMPLETED')
  const sum = (rows: Payment[]) => rows.reduce((total, p) => total + Number(p.amount_cents), 0)
  const paidCents = sum(completed.filter(p => p.transaction_type === 'SALE'))
  const refundedCents = sum(
    completed.filter(p => p.transaction_type === 'REFUND' || p.transaction_type === 'PARTIAL_REFUND')
  )
  return { paidCents, refundedCents, remainingCents: paidCents - refundedCents }
}

const toInputValue = (cents: number) => (cents / 100).toFixed(2)

const ExternalRefundModal: React.FC<ExternalRefundModalProps> = ({
  onClose,
  order,
  onConfirm,
  isLoading = false,
}) => {
  const { paidCents, refundedCents, remainingCents } = externalRefundTotals(order)
  const provider = externalRefundProvider(order)
  const [amount, setAmount] = useState(toInputValue(remainingCents))
  const [error, setError] = useState<string | null>(null)

  const handleAmountChange = (value: string) => {
    // Digits and one decimal point with at most two decimals — same cleaning as RefundModal.
    const cleaned = value.replace(/[^\d.]/g, '')
    const parts = cleaned.split('.')
    if (parts.length > 2) return
    if (parts[1] && parts[1].length > 2) return
    setAmount(cleaned)
    setError(null)
  }

  const handleConfirm = async () => {
    setError(null)
    const amountCents = Math.round(parseFloat(amount) * 100)
    if (!amount || !Number.isFinite(amountCents) || amountCents <= 0) {
      setError('Enter the amount that was refunded')
      return
    }
    if (amountCents > remainingCents) {
      setError(`The amount cannot exceed ${formatEuros(remainingCents)}, which is what is still paid on this order`)
      return
    }
    try {
      await onConfirm(amountCents)
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to record the refund')
    }
  }

  // Nothing closes the modal while the request runs: a refusal that arrives after an unmount
  // would be shown nowhere, and the Owner could not tell whether the refund was recorded.
  const closeUnlessBusy = isLoading ? undefined : onClose

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={closeUnlessBusy} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Record External Refund</h3>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500" disabled={isLoading}>
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-md p-3 mb-4">
              <div className="flex">
                <InformationCircleIcon className="h-5 w-5 text-blue-400 flex-shrink-0" />
                <p className="ml-3 text-sm text-blue-800">
                  This refund was made {refundedWhere(order)}. Nothing is sent to {provider} from here:
                  the amount is recorded so our books match.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <dl className="grid grid-cols-3 gap-2 text-sm">
                <div>
                  <dt className="font-medium text-gray-500">Paid</dt>
                  <dd className="mt-1 font-semibold text-gray-900">{formatEuros(paidCents)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Already refunded</dt>
                  <dd className="mt-1 font-semibold text-gray-900">{formatEuros(refundedCents)}</dd>
                </div>
                <div>
                  <dt className="font-medium text-gray-500">Still paid</dt>
                  <dd className="mt-1 font-semibold text-gray-900">{formatEuros(remainingCents)}</dd>
                </div>
              </dl>

              <div>
                <label htmlFor="external-refund-amount" className="block text-sm font-medium text-gray-700 mb-2">
                  Refunded amount <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <span className="text-gray-500 sm:text-sm">€</span>
                    </div>
                    <input
                      type="text"
                      id="external-refund-amount"
                      value={amount}
                      onChange={e => handleAmountChange(e.target.value)}
                      placeholder="0.00"
                      className="block w-full pl-7 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                      disabled={isLoading}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setAmount(toInputValue(remainingCents))
                      setError(null)
                    }}
                    disabled={isLoading}
                    className="px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50"
                  >
                    Full remainder
                  </button>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Less than the remainder records a partial refund; the full remainder marks the order
                  refunded. Several partial refunds are allowed.
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-3">
                  <p className="text-sm text-red-800">{error}</p>
                </div>
              )}
            </div>
          </div>

          <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={isLoading || !amount || parseFloat(amount) <= 0}
              className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Recording...' : 'Record Refund'}
            </button>
            <button
              type="button"
              onClick={onClose}
              disabled={isLoading}
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ExternalRefundModal
